import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Gift, Copy, Check, QrCode, Users, Trophy, X, ArrowLeft, Star, ShoppingBag, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { toast } from 'sonner';
import QRCode from 'qrcode';

const API = process.env.REACT_APP_BACKEND_URL || '';
const SITE_URL = typeof window !== 'undefined' ? window.location.origin : '';

const ReferralRewards = () => {
  const navigate = useNavigate();
  const [referralInfo, setReferralInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [editing, setEditing] = useState(false);
  const [newCode, setNewCode] = useState('');
  const [qrDataUrl, setQrDataUrl] = useState('');
  const [showQR, setShowQR] = useState(false);
  const [tiers, setTiers] = useState([]);
  const [pointsMatrix, setPointsMatrix] = useState([]);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const h = { Authorization: `Bearer ${token}` };
      const [infoRes, tiersRes, matrixRes] = await Promise.all([
        fetch(`${API}/api/referral/my-info`, { headers: h }),
        fetch(`${API}/api/referral/tiers`),
        fetch(`${API}/api/referral/points-matrix`),
      ]);
      if (infoRes.ok) setReferralInfo(await infoRes.json());
      if (tiersRes.ok) { const d = await tiersRes.json(); setTiers(d.tiers || []); }
      if (matrixRes.ok) { const d = await matrixRes.json(); setPointsMatrix(d.matrix || []); }
    } catch (err) { /* ignore */ }
    finally { setLoading(false); }
  };

  const referralLink = referralInfo ? `${SITE_URL}/plan/free?ref=${referralInfo.referral_code}` : '';

  const handleCopy = () => {
    navigator.clipboard.writeText(referralLink);
    setCopied(true);
    toast.success('Link copied!');
    setTimeout(() => setCopied(false), 2000);
  };

  const handleCustomize = async () => {
    if (!newCode.trim()) return;
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/api/referral/customize-code`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: newCode })
      });
      if (res.ok) { toast.success('Code updated!'); setEditing(false); fetchData(); }
      else { const err = await res.json(); toast.error(err.detail || 'Failed'); }
    } catch { toast.error('Failed to update'); }
  };

  const generateQR = async () => {
    try {
      const url = await QRCode.toDataURL(referralLink, { width: 300, margin: 2, color: { dark: '#292524', light: '#ffffff' } });
      setQrDataUrl(url);
      setShowQR(true);
    } catch { toast.error('Failed to generate QR'); }
  };

  const shareUrl = encodeURIComponent(referralLink);
  const shareText = encodeURIComponent('Join me on Thrive - the smart beekeeping management app. It\'s free!');

  const totalPoints = referralInfo?.total_points || 0;
  const currentTier = referralInfo?.current_tier;
  const nextTier = referralInfo?.next_tier;

  if (loading) return <div className="min-h-screen honeycomb-bg flex items-center justify-center"><div className="text-stone-500">Loading...</div></div>;

  return (
    <div className="min-h-screen honeycomb-bg pb-24" data-testid="referral-rewards-page">
      <div className="max-w-4xl mx-auto p-4 space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <button onClick={() => navigate('/')} className="p-2 hover:bg-stone-100 rounded-lg">
            <ArrowLeft className="h-5 w-5 text-stone-600" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-stone-900">Pollinate & Prosper</h1>
            <p className="text-sm text-stone-500">Invite fellow beekeepers and earn rewards</p>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card className="bg-white">
            <CardContent className="p-4 text-center">
              <div className="text-3xl font-black text-amber-600">{referralInfo?.confirmed_referrals || 0}</div>
              <div className="text-xs text-stone-500 mt-1">Confirmed</div>
            </CardContent>
          </Card>
          <Card className="bg-white">
            <CardContent className="p-4 text-center">
              <div className="text-3xl font-black text-blue-600">{referralInfo?.pending_referrals || 0}</div>
              <div className="text-xs text-stone-500 mt-1">Pending</div>
            </CardContent>
          </Card>
          <Card className="bg-white">
            <CardContent className="p-4 text-center">
              <div className="text-3xl font-black text-green-600">{referralInfo?.total_referrals || 0}</div>
              <div className="text-xs text-stone-500 mt-1">Total</div>
            </CardContent>
          </Card>
          <Card className="bg-white">
            <CardContent className="p-4 text-center">
              <div className="text-3xl font-black text-purple-600">{totalPoints}</div>
              <div className="text-xs text-stone-500 mt-1">Points</div>
            </CardContent>
          </Card>
          <Card className="bg-white">
            <CardContent className="p-4 text-center">
              <div className="text-2xl">{currentTier?.icon || '🌱'}</div>
              <div className="text-xs text-stone-500 mt-1">{currentTier?.name || 'Get started!'}</div>
            </CardContent>
          </Card>
        </div>

        {/* Your Referral Link */}
        <Card className="bg-white">
          <CardContent className="p-6 space-y-4">
            <h2 className="font-bold text-stone-800 flex items-center gap-2">
              <Gift className="h-5 w-5 text-amber-500" />
              Your Referral Link
            </h2>
            
            <div className="space-y-3">
              {/* Code */}
              <div>
                <label className="text-xs text-stone-500 mb-1 block">Your code</label>
                {!editing ? (
                  <div className="flex items-center gap-2">
                    <code className="flex-1 bg-amber-50 border border-amber-200 px-4 py-3 rounded-xl font-mono text-lg font-bold text-amber-800 text-center">
                      {referralInfo?.referral_code}
                    </code>
                    <Button variant="outline" size="sm" onClick={() => { setEditing(true); setNewCode(referralInfo?.referral_code || ''); }}>
                      Customize
                    </Button>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <Input value={newCode} onChange={e => setNewCode(e.target.value.toUpperCase().replace(/[^A-Z0-9-]/g, ''))} className="font-mono text-lg" maxLength={20} />
                    <Button onClick={handleCustomize} className="bg-amber-500 hover:bg-amber-600">Save</Button>
                    <Button variant="ghost" onClick={() => setEditing(false)}><X className="h-4 w-4" /></Button>
                  </div>
                )}
              </div>

              {/* Link */}
              <div>
                <label className="text-xs text-stone-500 mb-1 block">Share this link</label>
                <div className="flex items-center gap-2">
                  <div className="flex-1 bg-stone-50 border rounded-xl px-4 py-3 text-sm text-stone-600 truncate font-mono">
                    {referralLink}
                  </div>
                  <Button onClick={handleCopy} variant="outline" className="shrink-0" data-testid="copy-referral-link">
                    {copied ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
                    {copied ? 'Copied!' : 'Copy'}
                  </Button>
                </div>
              </div>

              {/* Share Buttons */}
              <div>
                <label className="text-xs text-stone-500 mb-2 block">Share via</label>
                <div className="flex flex-wrap gap-2">
                  <a href={`https://wa.me/?text=${shareText}%20${shareUrl}`} target="_blank" rel="noopener noreferrer"
                     className="flex items-center gap-2 px-4 py-2.5 bg-green-500 text-white rounded-xl text-sm font-medium hover:bg-green-600">
                    WhatsApp
                  </a>
                  <a href={`https://www.facebook.com/sharer/sharer.php?u=${shareUrl}`} target="_blank" rel="noopener noreferrer"
                     className="flex items-center gap-2 px-4 py-2.5 bg-blue-600 text-white rounded-xl text-sm font-medium hover:bg-blue-700">
                    Facebook
                  </a>
                  <a href={`https://twitter.com/intent/tweet?text=${shareText}&url=${shareUrl}`} target="_blank" rel="noopener noreferrer"
                     className="flex items-center gap-2 px-4 py-2.5 bg-stone-900 text-white rounded-xl text-sm font-medium hover:bg-stone-800">
                    X / Twitter
                  </a>
                  <a href={`mailto:?subject=${encodeURIComponent('Try Thrive - Free Beekeeping App!')}&body=${shareText}%20${shareUrl}`}
                     className="flex items-center gap-2 px-4 py-2.5 bg-stone-500 text-white rounded-xl text-sm font-medium hover:bg-stone-600">
                    Email
                  </a>
                  <button onClick={generateQR} className="flex items-center gap-2 px-4 py-2.5 bg-purple-100 text-purple-700 rounded-xl text-sm font-medium hover:bg-purple-200">
                    <QrCode className="h-4 w-4" /> QR Code
                  </button>
                </div>
              </div>

              {/* QR Code */}
              {showQR && qrDataUrl && (
                <div className="bg-white border-2 border-stone-200 rounded-2xl p-6 text-center">
                  <img src={qrDataUrl} alt="Referral QR" className="mx-auto rounded-xl shadow-md" />
                  <p className="text-sm text-stone-500 mt-3">Scan to sign up with your referral</p>
                  <button onClick={() => setShowQR(false)} className="text-xs text-stone-400 hover:text-stone-600 mt-2">Hide</button>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Reward Tiers */}
        {tiers.length > 0 && (
        <Card className="bg-white">
          <CardContent className="p-6">
            <h2 className="font-bold text-stone-800 flex items-center gap-2 mb-4">
              <Trophy className="h-5 w-5 text-amber-500" />
              Reward Tiers
            </h2>
            <div className="grid md:grid-cols-2 gap-3">
              {tiers.map(tier => {
                const achieved = totalPoints >= tier.points_required;
                const isCurrent = currentTier?.id === tier.id;
                const colorMap = { green: 'bg-green-50 border-green-200 text-green-700', yellow: 'bg-yellow-50 border-yellow-200 text-yellow-700', amber: 'bg-amber-50 border-amber-200 text-amber-700', purple: 'bg-purple-50 border-purple-200 text-purple-700', blue: 'bg-blue-50 border-blue-200 text-blue-700', teal: 'bg-teal-50 border-teal-200 text-teal-700' };
                return (
                  <div key={tier.id} className={`p-4 rounded-xl border-2 ${achieved ? (colorMap[tier.color] || colorMap.amber) : 'bg-stone-50 border-stone-200 text-stone-400'} ${isCurrent ? 'ring-2 ring-amber-400' : ''}`}>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xl">{tier.icon}</span>
                      <span className="font-bold">{tier.name}</span>
                      {isCurrent && <span className="text-xs bg-amber-200 text-amber-800 px-2 py-0.5 rounded-full ml-auto">Current</span>}
                    </div>
                    <p className="text-xs mb-2">{tier.points_required} points required</p>
                    {tier.free_rewards && (
                      <div className="space-y-0.5">
                        {tier.free_rewards.extra_hives > 0 && <p className="text-xs">+{tier.free_rewards.extra_hives} hives</p>}
                        {tier.free_rewards.extra_apiaries > 0 && <p className="text-xs">+{tier.free_rewards.extra_apiaries} apiaries</p>}
                        {tier.free_rewards.features?.length > 0 && <p className="text-xs">{tier.free_rewards.features.join(', ')}</p>}
                      </div>
                    )}
                    {tier.paid_rewards?.description && <p className="text-xs mt-1 font-medium">{tier.paid_rewards.description}</p>}
                  </div>
                );
              })}
            </div>
            {nextTier && (
              <p className="text-sm text-stone-500 mt-4 text-center">
                {nextTier.points_required - totalPoints} more point{nextTier.points_required - totalPoints > 1 ? 's' : ''} to reach <strong>{nextTier.name}</strong> {nextTier.icon}
              </p>
            )}
          </CardContent>
        </Card>
        )}

        {/* Coming Soon: Partnerships */}
        <Card className="bg-white border-dashed border-2 border-stone-300">
          <CardContent className="p-6">
            <h2 className="font-bold text-stone-800 flex items-center gap-2 mb-2">
              <ShoppingBag className="h-5 w-5 text-teal-500" />
              Shop Partnerships
              <span className="text-xs bg-teal-100 text-teal-700 px-2 py-0.5 rounded-full ml-2">Coming Soon</span>
            </h2>
            <p className="text-sm text-stone-500 mb-4">
              Partner with local bee shops to earn mutual rewards. They share your referral link with customers, 
              you drive traffic to their shop. Everyone wins!
            </p>
            <div className="grid md:grid-cols-3 gap-3">
              <div className="bg-stone-50 rounded-lg p-3 text-center">
                <div className="text-2xl mb-1">🏪</div>
                <p className="text-xs text-stone-600 font-medium">Featured Listing</p>
                <p className="text-xs text-stone-400">In Marketplace</p>
              </div>
              <div className="bg-stone-50 rounded-lg p-3 text-center">
                <div className="text-2xl mb-1">🎟</div>
                <p className="text-xs text-stone-600 font-medium">Discount Codes</p>
                <p className="text-xs text-stone-400">For customers</p>
              </div>
              <div className="bg-stone-50 rounded-lg p-3 text-center">
                <div className="text-2xl mb-1">📊</div>
                <p className="text-xs text-stone-600 font-medium">Shared Analytics</p>
                <p className="text-xs text-stone-400">Track together</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Rewards on Offer */}
        <Card className="bg-white border-dashed border-2 border-stone-300">
          <CardContent className="p-6">
            <h2 className="font-bold text-stone-800 flex items-center gap-2 mb-2">
              <Sparkles className="h-5 w-5 text-amber-500" />
              Rewards on Offer
              <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full ml-2">Coming Soon</span>
            </h2>
            <p className="text-sm text-stone-500 mb-4">
              Exclusive perks for our top referrers. The more beekeepers you bring, the more you earn!
            </p>
            <div className="grid md:grid-cols-2 gap-3">
              <div className="bg-amber-50 rounded-lg p-3 flex items-center gap-3">
                <div className="text-2xl">⬆️</div>
                <div><p className="text-sm font-medium text-amber-800">Plan Upgrades</p><p className="text-xs text-amber-600">Free months on higher tiers</p></div>
              </div>
              <div className="bg-amber-50 rounded-lg p-3 flex items-center gap-3">
                <div className="text-2xl">🎨</div>
                <div><p className="text-sm font-medium text-amber-800">Exclusive Features</p><p className="text-xs text-amber-600">Early access to new tools</p></div>
              </div>
              <div className="bg-amber-50 rounded-lg p-3 flex items-center gap-3">
                <div className="text-2xl">🏷</div>
                <div><p className="text-sm font-medium text-amber-800">Merch & Swag</p><p className="text-xs text-amber-600">Thrive branded gear</p></div>
              </div>
              <div className="bg-amber-50 rounded-lg p-3 flex items-center gap-3">
                <div className="text-2xl">🌟</div>
                <div><p className="text-sm font-medium text-amber-800">Community VIP</p><p className="text-xs text-amber-600">Featured in Hive Mind</p></div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Referral History */}
        {referralInfo?.referrals?.length > 0 && (
          <Card className="bg-white">
            <CardContent className="p-6">
              <h2 className="font-bold text-stone-800 flex items-center gap-2 mb-4">
                <Users className="h-5 w-5 text-stone-500" />
                Referral History
              </h2>
              <div className="space-y-2">
                {referralInfo.referrals.map(r => (
                  <div key={r.id} className="flex items-center justify-between bg-stone-50 rounded-xl px-4 py-3">
                    <div>
                      <span className="text-sm font-medium text-stone-800">{r.referred_name || r.referred_email}</span>
                      <span className="text-xs text-stone-400 ml-2">{r.created_at ? new Date(r.created_at).toLocaleDateString() : ''}</span>
                    </div>
                    <span className={`text-xs px-3 py-1 rounded-full font-medium ${r.status === 'confirmed' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                      {r.status}
                    </span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default ReferralRewards;
